/**
 * Gauge de performance das campanhas — implementação customizada em Canvas 2D API.
 *
 * Interface pública mantida: `module.exports = { renderCampaignGauge }`.
 * O `campaigns-list.js` continua chamando `renderCampaignGauge(campaignId)` com
 * o mesmo signature — este arquivo é a única dependência.
 */

const gaugeInstances = {};

// --- Constantes visuais -------------------------------------------------
const START_ANGLE_DEG = 45;       
const SWEEP_DEG = 270;            // semicircular 270°
const CANVAS_OFFSET_DEG = 90;     
const ARC_THICKNESS = 11;         // barWidth
const TICK_LENGTH = 8;
const TICK_WIDTH = 2;
const COLOR_TRACK = '#e0e0e0';
const COLOR_GRADIENT_START = '#29b6f6';
const COLOR_GRADIENT_END = '#0a4781';
const COLOR_TICK = '#bdbdbd';
const COLOR_NUMBERS = '#424242';
const COLOR_NEEDLE = '#29b6f6';
const COLOR_PIVOT_OUTER = '#29b6f6';
const COLOR_PIVOT_INNER = '#0a4781';
const NEEDLE_SHADOW = 'rgba(41, 182, 246, 0.25)';
const PROGRESS_SEGMENTS = 120;
const ANIMATION_DURATION = 900;

const formatScore = function (value) {
    return Math.round(value).toLocaleString('pt-BR');
};

const resolveGaugeSize = function (wrap) {
    const chart = wrap ? wrap.querySelector('.campaign-gauge-chart') : null;
    const container = chart || wrap;
    const width = container
        ? Math.min(Math.max(container.clientWidth || 260, 220), 280)
        : 260;

    return {
        width: width,
        height: Math.round(width * 0.78),
    };
};

const resolveValueFontSize = function (score) {
    if (score >= 1000000) {
        return '1.1rem';
    }

    if (score >= 100000) {
        return '1.2rem';
    }

    if (score >= 10000) {
        return '1.3rem';
    }

    return '1.4rem';
};

const buildMajorTicks = function (goal) {
    const steps = goal <= 100 ? 10 : (goal <= 1000 ? 5 : 4);
    const ticks = [];

    for (let i = 0; i <= steps; i++) {
        ticks.push(Math.round((goal / steps) * i));
    }

    return ticks;
};

// --- Helpers de cor / geometria -----------------------------------------

const hexToRgb = function (hex) {
    const clean = hex.replace('#', '');

    return {
        r: parseInt(clean.substring(0, 2), 16),
        g: parseInt(clean.substring(2, 4), 16),
        b: parseInt(clean.substring(4, 6), 16),
    };
};

const lerpColor = function (startHex, endHex, t) {
    const start = hexToRgb(startHex);
    const end = hexToRgb(endHex);
    const clamped = Math.min(Math.max(t, 0), 1);

    const r = Math.round(start.r + (end.r - start.r) * clamped);
    const g = Math.round(start.g + (end.g - start.g) * clamped);
    const b = Math.round(start.b + (end.b - start.b) * clamped);

    return `rgb(${r}, ${g}, ${b})`;
};

const toRadians = function (degrees) {
    return (degrees * Math.PI) / 180;
};

const toCanvasAngle = function (labelDegrees) {
    return toRadians(labelDegrees + CANVAS_OFFSET_DEG);
};

// Easing: desaceleração quadrática (dequad).
const easeOutQuad = function (t) {
    return 1 - (1 - t) * (1 - t);
};

// --- Classe principal ---------------------------------------------------

const CampaignGauge = function (canvas, score, goal, width, height) {
    this.canvas = canvas;
    this.score = Math.max(0, Math.min(score, goal));
    this.goal = goal;
    this.width = width;
    this.height = height;
    this.majorTicks = buildMajorTicks(goal);
    this.animationFrame = null;
    this.currentValue = 0;
};

CampaignGauge.prototype.getGeometry = function () {
    const centerX = this.width / 2;
    // Centro vertical ligeiramente abaixo do topo para acomodar a leitura
    // dos números internos sem colidir com o pivot/score central.
    const centerY = this.height * 0.52;
    const radius = Math.min(this.width / 2, this.height / 2) * 0.88;

    return {
        centerX: centerX,
        centerY: centerY,
        radius: radius,
        startAngle: toCanvasAngle(START_ANGLE_DEG),
        endAngle: toCanvasAngle(START_ANGLE_DEG + SWEEP_DEG),
    };
};

CampaignGauge.prototype.valueToCanvasAngle = function (value) {
    const fraction = this.goal > 0 ? Math.max(0, Math.min(value / this.goal, 1)) : 0;

    return toCanvasAngle(START_ANGLE_DEG + fraction * SWEEP_DEG);
};

CampaignGauge.prototype.drawTrack = function (ctx, geo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(geo.centerX, geo.centerY, geo.radius, geo.startAngle, geo.endAngle, false);
    ctx.lineWidth = ARC_THICKNESS;
    ctx.strokeStyle = COLOR_TRACK;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.restore();
};

CampaignGauge.prototype.drawProgress = function (ctx, geo, value) {
    const fraction = this.goal > 0 ? Math.max(0, Math.min(value / this.goal, 1)) : 0;

    if (fraction <= 0) {
        return;
    }

    const endAngle = toCanvasAngle(START_ANGLE_DEG + fraction * SWEEP_DEG);

    const fullSweep = geo.endAngle - geo.startAngle;

    ctx.save();
    ctx.lineWidth = ARC_THICKNESS;
    ctx.lineCap = 'butt'; // segmentos justapostos — round deixaria sobreposição

    for (let i = 0; i < PROGRESS_SEGMENTS; i++) {
        const segStart = i / PROGRESS_SEGMENTS;
        const segEnd = (i + 1) / PROGRESS_SEGMENTS;

        if (segStart >= fraction) {
            break;
        }

        const segEndClamped = Math.min(segEnd, fraction);
        const a1 = geo.startAngle + fullSweep * segStart;
        const a2 = geo.startAngle + fullSweep * segEndClamped;

        // Cor interpolada pelo ponto médio do segmento ao longo do arco.
        const colorT = (segStart + segEndClamped) / 2;
        ctx.beginPath();
        ctx.arc(geo.centerX, geo.centerY, geo.radius, a1, a2, false);
        ctx.strokeStyle = lerpColor(COLOR_GRADIENT_START, COLOR_GRADIENT_END, colorT / fraction);
        ctx.stroke();
    }

    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(geo.centerX, geo.centerY, geo.radius, geo.startAngle, geo.startAngle, false);
    ctx.strokeStyle = lerpColor(COLOR_GRADIENT_START, COLOR_GRADIENT_END, 0);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(geo.centerX, geo.centerY, geo.radius, endAngle, endAngle, false);
    ctx.strokeStyle = lerpColor(COLOR_GRADIENT_START, COLOR_GRADIENT_END, 1);
    ctx.stroke();
    ctx.restore();
};

CampaignGauge.prototype.pointOnArc = function (geo, angle) {
    return {
        x: geo.centerX + geo.radius * Math.cos(angle),
        y: geo.centerY + geo.radius * Math.sin(angle),
    };
};

CampaignGauge.prototype.drawTicks = function (ctx, geo) {
    ctx.save();
    ctx.strokeStyle = COLOR_TICK;
    ctx.lineWidth = TICK_WIDTH;
    ctx.lineCap = 'butt';

    // Ticks INTERNOS ao arco: começam na linha do arco e estendem ~8px em
    // direção ao centro (para dentro), conforme o mockup de referência.
    for (let i = 0; i < this.majorTicks.length; i++) {
        const tickValue = this.majorTicks[i];
        const angle = this.valueToCanvasAngle(tickValue);
        const outer = geo.radius;
        const inner = geo.radius - TICK_LENGTH;

        ctx.beginPath();
        ctx.moveTo(
            geo.centerX + outer * Math.cos(angle),
            geo.centerY + outer * Math.sin(angle)
        );
        ctx.lineTo(
            geo.centerX + inner * Math.cos(angle),
            geo.centerY + inner * Math.sin(angle)
        );
        ctx.stroke();
    }

    ctx.restore();
};

CampaignGauge.prototype.drawNumbers = function (ctx, geo) {
    ctx.save();
    ctx.fillStyle = COLOR_NUMBERS;
    ctx.font = `${Math.round(this.width * 0.035)}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const labelRadius = geo.radius - TICK_LENGTH - Math.max(12, Math.round(this.width * 0.035));

    for (let i = 0; i < this.majorTicks.length; i++) {
        const tickValue = this.majorTicks[i];
        const angle = this.valueToCanvasAngle(tickValue);

        const x = geo.centerX + labelRadius * Math.cos(angle);
        const y = geo.centerY + labelRadius * Math.sin(angle);

        ctx.fillText(formatScore(tickValue), x, y);
    }
    ctx.restore();
};

CampaignGauge.prototype.drawNeedle = function (ctx, geo, value) {
    const angle = this.valueToCanvasAngle(value);
    const needleLength = geo.radius * 0.76;
    const baseHalf = 2.5; // largura da base ≈ 5px

    ctx.save();
    ctx.translate(geo.centerX, geo.centerY);
    ctx.rotate(angle);

    // Sombra sutil do ponteiro.
    ctx.shadowColor = NEEDLE_SHADOW;
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;

    ctx.beginPath();
    ctx.moveTo(0, -baseHalf);
    ctx.lineTo(needleLength, 0);
    ctx.lineTo(0, baseHalf);
    ctx.closePath();
    ctx.fillStyle = COLOR_NEEDLE;
    ctx.fill();

    ctx.restore();

    // Pivot: anel externo + núcleo interno.
    const outerRadius = Math.max(8, Math.round(this.width * 0.054));
    const innerRadius = outerRadius * 0.75;

    ctx.save();
    ctx.beginPath();
    ctx.arc(geo.centerX, geo.centerY, outerRadius, 0, Math.PI * 2);
    ctx.fillStyle = COLOR_PIVOT_OUTER;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(geo.centerX, geo.centerY, innerRadius, 0, Math.PI * 2);
    ctx.fillStyle = COLOR_PIVOT_INNER;
    ctx.fill();
    ctx.restore();
};

CampaignGauge.prototype.clear = function () {
    const dpr = window.devicePixelRatio || 1;
    const ctx = this.canvas.getContext('2d');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

CampaignGauge.prototype.render = function (value) {
    value = value === undefined ? this.score : value;
    const ctx = this.canvas.getContext('2d');
    const geo = this.getGeometry();

    this.clear();
    this.drawTrack(ctx, geo);
    this.drawProgress(ctx, geo, value);
    this.drawTicks(ctx, geo);
    this.drawNumbers(ctx, geo);
    this.drawNeedle(ctx, geo, value);
};

CampaignGauge.prototype.animate = function () {
    const self = this;

    self.render(self.score);
    self.currentValue = self.score;

    const startTime = performance.now();

    const step = function (now) {
        const elapsed = now - startTime;
        const linear = Math.min(elapsed / ANIMATION_DURATION, 1);
        const eased = easeOutQuad(linear);
        const value = self.score * eased;

        self.render(value);

        if (linear < 1) {
            self.animationFrame = requestAnimationFrame(step);
        } else {
            self.animationFrame = null;
            self.currentValue = self.score;
            self.render(self.score);
        }
    };

    self.animationFrame = requestAnimationFrame(step);
};

CampaignGauge.prototype.destroy = function () {
    if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
    }

    if (this.canvas) {
        const ctx = this.canvas.getContext('2d');

        if (ctx) {
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
    }
};

// --- Entrada pública ----------------------------------------------------

const renderCampaignGauge = function (campaignId) {
    const canvas = document.getElementById(`campaign-gauge-${campaignId}`);

    if (!canvas || !canvas.getContext) {
        return;
    }

    const wrap = canvas.closest('.campaign-gauge-wrap');
    const scoreEl = wrap ? wrap.querySelector('.campaign-gauge-score') : null;
    const minEl = wrap ? wrap.querySelector('.campaign-gauge-limit--min') : null;
    const maxEl = wrap ? wrap.querySelector('.campaign-gauge-limit--max') : null;
    const score = Math.max(0, parseFloat(canvas.dataset.score) || 0);
    const goal = Math.max(0, parseFloat(canvas.dataset.goal) || 0);

    if (goal <= 0) {
        return;
    }

    const size = resolveGaugeSize(wrap);

    if (gaugeInstances[campaignId]) {
        gaugeInstances[campaignId].destroy();
        delete gaugeInstances[campaignId];
    }

    if (scoreEl) {
        scoreEl.textContent = formatScore(score);
        scoreEl.style.fontSize = resolveValueFontSize(score);
    }

    if (minEl) {
        minEl.style.display = 'none';
    }

    if (maxEl) {
        maxEl.style.display = 'none';
    }

    const dpr = window.devicePixelRatio || 1;

    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);

    const chart = wrap ? wrap.querySelector('.campaign-gauge-chart') : null;

    if (chart) {
        chart.style.width = `${size.width}px`;
        chart.style.height = `${size.height}px`;
    }

    const ctx = canvas.getContext('2d');

    if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const gauge = new CampaignGauge(canvas, score, goal, size.width, size.height);
    gauge.animate();
    gaugeInstances[campaignId] = gauge;
};

module.exports = {
    renderCampaignGauge,
};